import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import QuickAddModal from "@/components/QuickAddModal";
import CommandPalette from "@/components/CommandPalette";
import {
  CalendarBlank, CalendarCheck, ClockCounterClockwise,
  Plus, SignOut, SquaresFour, Tag, Palette, FileArrowDown, ChartLineUp, MagnifyingGlass,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/today", label: "Vandaag", icon: CalendarCheck, testid: "nav-today" },
  { to: "/week", label: "Week", icon: SquaresFour, testid: "nav-week" },
  { to: "/month", label: "Maand", icon: CalendarBlank, testid: "nav-month" },
  { to: "/routines", label: "Routines", icon: ClockCounterClockwise, testid: "nav-routines" },
  { to: "/categories", label: "Categorieën", icon: Tag, testid: "nav-categories" },
  { to: "/import", label: "Importeren", icon: FileArrowDown, testid: "nav-import" },
  { to: "/recap", label: "Terugblik", icon: ChartLineUp, testid: "nav-recap" },
  { to: "/settings", label: "Instellingen", icon: Palette, testid: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [quickOpen, setQuickOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#0B1120] text-slate-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-60 flex-col border-r border-slate-800 bg-[#0B1120] sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-800">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-600/15 text-blue-300 border border-blue-500/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border border-transparent"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <Button
            data-testid="cmd-open-button"
            onClick={() => setPaletteOpen(true)}
            variant="outline"
            className="w-full border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-300 justify-between gap-2 mb-2"
          >
            <span className="flex items-center gap-2 text-sm"><MagnifyingGlass size={14} /> Zoeken</span>
            <span className="text-[10px] font-mono text-slate-500 px-1.5 py-0.5 rounded border border-slate-700">⌘K</span>
          </Button>
          <Button
            data-testid="quick-add-button-desktop"
            onClick={() => setQuickOpen(true)}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white gap-2"
          >
            <Plus size={16} weight="bold" /> Snel toevoegen
          </Button>
          <div className="mt-4 flex items-center justify-between text-xs text-slate-500 px-2">
            <div className="truncate">
              <div className="text-slate-300 font-medium truncate">{user?.name}</div>
              <div className="truncate">{user?.email}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/auth"); }}
              className="p-2 rounded-md hover:bg-slate-800/50 text-slate-400 hover:text-slate-100"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0B1120]/85 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo size={22} />
          <Button
            data-testid="quick-add-button-mobile"
            onClick={() => setQuickOpen(true)}
            size="sm"
            className="bg-blue-600 hover:bg-blue-500 text-white gap-1 h-8 px-3"
          >
            <Plus size={14} weight="bold" /> Nieuw
          </Button>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0 pb-20 lg:pb-0">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0B1120]/90 backdrop-blur-xl border-t border-slate-800">
        <div className="grid grid-cols-5">
          {NAV.slice(0, 5).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${
                  isActive ? "text-blue-400" : "text-slate-500"
                }`
              }
            >
              <n.icon size={20} weight={"duotone"} />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <QuickAddModal open={quickOpen} onOpenChange={setQuickOpen} />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onQuickAdd={() => { setPaletteOpen(false); setQuickOpen(true); }}
      />
    </div>
  );
}
